import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Checkbox from "@cloudscape-design/components/checkbox"
import Icon from "@cloudscape-design/components/icon"
import Input from "@cloudscape-design/components/input"
import Modal from "@cloudscape-design/components/modal"
import RadioButton from "@cloudscape-design/components/radio-button"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import Table, { type TableProps } from "@cloudscape-design/components/table"
import { useState, useEffect } from "react"
import type { CodelistItem } from "@/generated/sdk"
import {
  useTerminologyCodelist,
  useTerminologySearchPages,
} from "../../../hooks/queries/useTerminology"
import { type LibraryCodelist, terminologyApiService } from "../../../services/terminologyApi"

interface AccordionRowProps {
  item: LibraryCodelist
  selected: boolean
  onToggle: () => void
  mode?: "import" | "pick"
}

const valueColumns: TableProps.ColumnDefinition<{ code: string; decode: string }>[] = [
  { id: "code", header: "Code", cell: (v) => <Box variant="code">{v.code}</Box> },
  { id: "decode", header: "Decode", cell: (v) => v.decode },
]

const AccordionRow: React.FC<AccordionRowProps> = ({ item, selected, onToggle, mode }) => {
  const [expanded, setExpanded] = useState(false)
  // Fetch the full value list lazily, only once the row has been expanded.
  const { data: codelist, isError } = useTerminologyCodelist(item.code, expanded)
  const values = codelist?.values ?? (isError ? [] : null)

  const handleExpand = () => setExpanded((prev) => !prev)

  return (
    <div>
      <SpaceBetween size="xs" direction="horizontal" alignItems="center">
        {mode === "pick" ? (
          <RadioButton
            checked={selected}
            onSelect={onToggle}
            name="sdtm-pick"
            nativeInputAttributes={{ "aria-label": item.name }}
          />
        ) : (
          <Checkbox checked={selected} onChange={onToggle} ariaLabel={item.name} />
        )}
        <Button variant="inline-link" onClick={handleExpand}>
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Icon name={expanded ? "caret-down-filled" : "caret-right-filled"} size="small" />
            <Box variant="strong">{item.name}</Box>
            <Box color="text-body-secondary" fontSize="body-s">
              {item.value_count} values
            </Box>
            {item.extensible && <Badge color="severity-medium">ext</Badge>}
          </SpaceBetween>
        </Button>
      </SpaceBetween>
      {expanded && (
        <Box padding={{ left: "xl", top: "xs", bottom: "s" }}>
          {!values ? (
            <Box color="text-body-secondary">Loading...</Box>
          ) : (
            <Table
              variant="embedded"
              columnDefinitions={valueColumns}
              items={values}
              trackBy="code"
            />
          )}
        </Box>
      )}
    </div>
  )
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onImport: (items: CodelistItem[]) => void
  existingNames: string[]
  mode?: "import" | "pick"
}

export const SDTMLibraryModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onImport,
  existingNames,
  mode = "import",
}) => {
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  // Number of pages loaded for the current query; "load more" bumps this.
  const [pageCount, setPageCount] = useState(1)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [importing, setImporting] = useState(false)
  const [conflicts, setConflicts] = useState<string[]>([])

  // Debounce the search box; reset paging whenever the query changes.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPageCount(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // Load each page for the current query and accumulate them into one list. Each page is cached by
  // (query, offset), so paging is cheap and re-opening the modal reuses prior results.
  const pageQueries = useTerminologySearchPages(debouncedSearch, pageCount)
  const results: LibraryCodelist[] = pageQueries.flatMap((q) => q.data?.items ?? [])
  const total = pageQueries[0]?.data?.total ?? 0
  const loading = pageQueries.some((q) => q.isLoading)

  const toggleSelect = (code: string) => {
    if (mode === "pick") {
      setSelected(new Set([code]))
      return
    }
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  const handleImport = async () => {
    // Check for conflicts before fetching
    const selectedNames = results.filter((r) => selected.has(r.code)).map((r) => r.name)
    const found = selectedNames.filter((name) =>
      existingNames.some((e) => e.toLowerCase() === name.toLowerCase()),
    )
    if (found.length > 0 && conflicts.length === 0) {
      setConflicts(found)
      return
    }
    setConflicts([])
    setImporting(true)
    try {
      const items: CodelistItem[] = []
      for (const code of selected) {
        const full = await terminologyApiService.getByCode(code)
        items.push({
          id: crypto.randomUUID(),
          name: full.name,
          column: full.submission_value,
          extensible: full.extensible,
          severity: "major",
          source: "sdtm-library",
          source_id: full.code,
          conditions: [],
          values: full.values.map((v) => ({
            code: v.code,
            decode: v.decode,
            other_value: "",
          })),
        })
      }
      onImport(items)
      setSelected(new Set())
      onClose()
    } catch (e) {
      console.error("Failed to import codelists:", e)
    } finally {
      setImporting(false)
    }
  }

  if (!isOpen) return null

  const footer = (
    <Box float="right">
      <SpaceBetween size="xs" direction="horizontal">
        <Button variant="link" onClick={onClose}>
          Cancel
        </Button>
        {mode !== "pick" && (
          <Button
            variant="primary"
            onClick={() => void handleImport()}
            disabled={selected.size === 0}
            loading={importing}
          >
            {importing
              ? "Importing..."
              : `Import ${selected.size} codelist${selected.size !== 1 ? "s" : ""}`}
          </Button>
        )}
        {mode === "pick" && (
          <Button
            variant="primary"
            disabled={selected.size === 0}
            onClick={() => {
              const code = [...selected][0]
              const item = results.find((r) => r.code === code)
              if (item) {
                onImport([
                  {
                    id: crypto.randomUUID(),
                    name: item.name,
                    column: item.submission_value,
                    extensible: item.extensible,
                    severity: "major",
                    source: "sdtm-library",
                    source_id: item.code,
                    conditions: [],
                    values: [],
                  },
                ])
              }
              onClose()
            }}
          >
            OK
          </Button>
        )}
      </SpaceBetween>
    </Box>
  )

  return (
    <>
      <Modal
        visible
        onDismiss={onClose}
        closeAriaLabel="Close"
        size="large"
        header={mode === "pick" ? "Select SDTM Codelist" : "Import from SDTM Library"}
        footer={footer}
      >
        <SpaceBetween size="s">
          <div>
            <Input
              type="search"
              placeholder="Search codelists (e.g. sex, race, country)..."
              value={search}
              onChange={({ detail }) => setSearch(detail.value)}
              ariaLabel="Search codelists"
              autoFocus
            />
            <Box color="text-body-secondary" fontSize="body-s" margin={{ top: "xxs" }}>
              {total} codelist{total !== 1 ? "s" : ""}
              {selected.size > 0 && ` · ${selected.size} selected`}
            </Box>
          </div>

          {/* Cloudscape has no infinite-scroll list primitive; a Table with progressive
              loading would change the selection DOM (per-row + select-all controls). Keep a
              scroll container that requests the next page as the user nears the bottom. */}
          <div
            style={{ height: "400px", overflowY: "auto" }}
            onScroll={(e) => {
              const el = e.currentTarget
              if (
                el.scrollHeight - el.scrollTop - el.clientHeight < 400 &&
                !loading &&
                results.length < total
              ) {
                setPageCount((c) => c + 1)
              }
            }}
          >
            {loading && results.length === 0 ? (
              <Box textAlign="center" color="text-body-secondary" padding="l">
                <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                  <Spinner /> <span>Searching...</span>
                </SpaceBetween>
              </Box>
            ) : results.length === 0 ? (
              <Box textAlign="center" color="text-body-secondary" padding="l">
                {search ? "No codelists found" : "No codelists available"}
              </Box>
            ) : (
              <SpaceBetween size="xs">
                {results.map((cl) => (
                  <AccordionRow
                    key={cl.code}
                    item={cl}
                    selected={selected.has(cl.code)}
                    onToggle={() => toggleSelect(cl.code)}
                    mode={mode}
                  />
                ))}
                {results.length < total && loading && (
                  <Box textAlign="center" color="text-body-secondary" fontSize="body-s">
                    Loading more...
                  </Box>
                )}
              </SpaceBetween>
            )}
          </div>
        </SpaceBetween>
      </Modal>

      {conflicts.length > 0 && (
        <Modal
          visible
          onDismiss={() => setConflicts([])}
          closeAriaLabel="Close"
          size="small"
          header="Replace existing codelists?"
          footer={
            <Box float="right">
              <SpaceBetween size="xs" direction="horizontal">
                <Button variant="link" onClick={() => setConflicts([])}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={() => void handleImport()}>
                  Replace
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="s">
            <Box>The following will be replaced with the SDTM standard version:</Box>
            <SpaceBetween size="xxs">
              {conflicts.map((name) => (
                <Box key={name} variant="strong">
                  {name}
                </Box>
              ))}
            </SpaceBetween>
          </SpaceBetween>
        </Modal>
      )}
    </>
  )
}
